// Actions · usePersistentNotice
//
// Keyed, non-closable, never-auto-dismissing AntD notification for long-running
// background work ("N dokumentów jest wgrywanych…"). Opening again with the same
// key updates the notice in place; it stays until `close(key)`. Wraps
// `App.useApp().notification` (the context-aware instance — never the static
// antd import) so app code composes this instead of reaching for antd. Content
// is wrapped in `<span data-testid={testId}>` so e2e specs assert by testid.
//
// Usage:
//   const notice = usePersistentNotice();
//   notice.open({ key: "upload", testId: "upload-progress-notice", content: "2 dokumenty…", icon: <LoadingOutlined spin /> });
//   notice.close("upload");

import React, { useMemo } from "react";
import { App } from "antd";

export interface PersistentNoticeArgs {
  /** Stable key — opening again with the same key updates the notice in place. */
  key: string;
  /** Stamped as `data-testid` on the content span. */
  testId: string;
  content: React.ReactNode;
  icon?: React.ReactNode;
}

export interface PersistentNotice {
  open: (args: PersistentNoticeArgs) => void;
  close: (key: string) => void;
}

export function usePersistentNotice(): PersistentNotice {
  const { notification } = App.useApp();

  // Stable identity (AntD's `notification` instance is itself stable) so
  // consumers can list `notice` in effect deps without re-running per render.
  return useMemo(
    () => ({
      open: ({ key, testId, content, icon }: PersistentNoticeArgs) => {
        notification.open({
          key,
          message: <span data-testid={testId}>{content}</span>,
          icon,
          duration: 0,
          closable: false,
          placement: "topRight",
          role: "status",
        });
      },
      close: (key: string) => notification.destroy(key),
    }),
    [notification],
  );
}
